import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const LAST_UPDATED = 'March 24, 2026';
const CONTACT_EMAIL = 'submit@scoremyoffer.com';

const TermsAndConditions: React.FC = () => {
  return (
    <div className="legal-page">
      {/* Top bar */}
      <header className="legal-topbar">
        <Link to="/" className="legal-brand">
          <div className="legal-brand-icon">★</div>
          <span className="legal-brand-name">Score My Offer</span>
        </Link>
        <Link to="/signup" className="legal-back-link">
          ← Back to Sign Up
        </Link>
      </header>

      {/* Content */}
      <main className="legal-content">
        <div className="legal-hero">
          <div className="legal-badge">📄 Legal</div>
          <h1>Terms &amp; Conditions</h1>
          <div className="legal-meta">
            <span className="legal-meta-item">
              <span className="legal-meta-icon">📅</span>
              Last updated: {LAST_UPDATED}
            </span>
            <span className="legal-meta-item">
              <span className="legal-meta-icon">🏢</span>
              Score My Offer
            </span>
          </div>
        </div>

        {/* Table of Contents */}
        <nav className="legal-toc">
          <h3>Contents</h3>
          <ol>
            <li><a href="#acceptance">Acceptance of Terms</a></li>
            <li><a href="#description">Description of Service</a></li>
            <li><a href="#eligibility">Eligibility &amp; Registration</a></li>
            <li><a href="#account">Account Responsibilities</a></li>
            <li><a href="#acceptable-use">Acceptable Use</a></li>
            <li><a href="#content">User Content &amp; Data</a></li>
            <li><a href="#ai-processing">AI-Powered Processing</a></li>
            <li><a href="#intellectual-property">Intellectual Property</a></li>
            <li><a href="#disclaimers">Disclaimers &amp; Limitations</a></li>
            <li><a href="#termination">Termination</a></li>
            <li><a href="#governing-law">Governing Law</a></li>
            <li><a href="#contact">Contact Us</a></li>
          </ol>
        </nav>

        {/* Sections */}
        <section className="legal-section" id="acceptance">
          <h2><span className="legal-section-num">1</span> Acceptance of Terms</h2>
          <p>
            By accessing or using Score My Offer ("the Platform," "we," "our," or "us"), you agree to be bound by these Terms &amp; Conditions ("Terms"). If you do not agree to these Terms, you may not use the Platform.
          </p>
          <p>
            These Terms constitute a legally binding agreement between you and Score My Offer. We may update these Terms from time to time; continued use of the Platform after changes are posted constitutes your acceptance of the revised Terms.
          </p>
        </section>

        <section className="legal-section" id="description">
          <h2><span className="legal-section-num">2</span> Description of Service</h2>
          <p>
            Score My Offer is a professional real estate offer management platform designed for licensed real estate professionals. The Platform provides:
          </p>
          <ul>
            <li>AI-powered analysis and scoring of buyer offer documents (purchase agreements and pre-approval letters)</li>
            <li>A Probability to Close (PTC) Score (0–10) based on financial strength, transactional risk, and professional vetting factors</li>
            <li>Side-by-side offer comparison tools for listing agents</li>
            <li>Lender verification and vetting via NMLS records</li>
            <li>Secure document storage and management</li>
            <li>Offer submission via web form and email</li>
          </ul>
          <p>
            The Platform is intended solely for use by licensed real estate agents, loan originators, and mortgage professionals in the United States.
          </p>
        </section>

        <section className="legal-section" id="eligibility">
          <h2><span className="legal-section-num">3</span> Eligibility &amp; Registration</h2>
          <p>
            To register for and use the Platform, you must:
          </p>
          <ul>
            <li>Be at least 18 years of age</li>
            <li>Hold a valid, active real estate license (for real estate agents) or NMLS license (for loan originators) in your state of practice</li>
            <li>Provide accurate, complete, and current registration information including your MLS or NMLS license number</li>
            <li>Verify your phone number via SMS during registration</li>
            <li>Verify your email address via the verification link sent upon registration</li>
          </ul>
          <p>
            Account access is granted only after admin review and approval. We reserve the right to decline or revoke access at our sole discretion.
          </p>
        </section>

        <section className="legal-section" id="account">
          <h2><span className="legal-section-num">4</span> Account Responsibilities</h2>
          <p>You are responsible for:</p>
          <ul>
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activity that occurs under your account</li>
            <li>Promptly notifying us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> of any unauthorized access or security breach</li>
            <li>Keeping your registration information accurate and up to date</li>
          </ul>
          <p>
            You may not share your account credentials or allow others to access the Platform using your credentials.
          </p>
        </section>

        <section className="legal-section" id="acceptable-use">
          <h2><span className="legal-section-num">5</span> Acceptable Use</h2>
          <p>You agree to use the Platform only for lawful professional purposes and in compliance with all applicable laws, regulations, and real estate industry standards. You may not:</p>
          <ul>
            <li>Upload documents that contain fraudulent, misleading, or fabricated information</li>
            <li>Use the Platform to facilitate any discriminatory practices in violation of the Fair Housing Act or other applicable laws</li>
            <li>Attempt to reverse-engineer, scrape, or extract data from the Platform</li>
            <li>Interfere with or disrupt the security, integrity, or performance of the Platform</li>
            <li>Use automated tools or bots to access the Platform without express written permission</li>
            <li>Share, sell, or redistribute any data or output from the Platform to unauthorized parties</li>
            <li>Impersonate any person or entity</li>
          </ul>
        </section>

        <section className="legal-section" id="content">
          <h2><span className="legal-section-num">6</span> User Content &amp; Data</h2>
          <h3>Your Documents</h3>
          <p>
            When you upload purchase agreements, pre-approval letters, MLS listing documents, or any other files ("User Content"), you grant Score My Offer a limited, non-exclusive license to process, store, and analyze that content solely for the purpose of providing the Platform's services.
          </p>
          <h3>Data Accuracy</h3>
          <p>
            You represent and warrant that all documents and information you submit are authentic, accurate, and that you have the authority to share them on the Platform. Score My Offer does not independently verify the authenticity of uploaded documents.
          </p>
          <h3>Data Retention</h3>
          <p>
            Offer documents and extracted data are retained for the duration of your account and for a reasonable period thereafter, as described in our Privacy Policy. You may request deletion of your data at any time by contacting us.
          </p>
          <div className="legal-highlight">
            <p>
              <strong>Important:</strong> Do not upload documents containing sensitive personal financial information beyond what is necessary for offer evaluation. Ensure you have appropriate authorization from all parties before submitting documents that contain third-party personal information.
            </p>
          </div>
        </section>

        <section className="legal-section" id="ai-processing">
          <h2><span className="legal-section-num">7</span> AI-Powered Processing</h2>
          <p>
            Score My Offer uses artificial intelligence, including the Claude AI model by Anthropic, to extract and analyze data from uploaded documents. You acknowledge and agree that:
          </p>
          <ul>
            <li>AI extraction is automated and may not be 100% accurate; extracted data should be reviewed and verified by a qualified professional before being relied upon</li>
            <li>The PTC Score is an analytical tool to assist professional judgment — it is not a guarantee of any transaction outcome and should not be the sole basis for accepting or rejecting an offer</li>
            <li>Document content may be transmitted to third-party AI processing services (such as Anthropic) under their respective terms of service and privacy policies</li>
            <li>Score My Offer is not liable for errors in AI-extracted data or PTC Scores</li>
          </ul>
        </section>

        <section className="legal-section" id="intellectual-property">
          <h2><span className="legal-section-num">8</span> Intellectual Property</h2>
          <p>
            The Platform, including its design, features, algorithms, scoring models, and software, is the exclusive intellectual property of Score My Offer and is protected by applicable copyright, trademark, and other intellectual property laws.
          </p>
          <p>
            You may not copy, reproduce, modify, distribute, or create derivative works based on any portion of the Platform without our prior written consent. All rights not expressly granted herein are reserved.
          </p>
        </section>

        <section className="legal-section" id="disclaimers">
          <h2><span className="legal-section-num">9</span> Disclaimers &amp; Limitations of Liability</h2>
          <h3>No Professional Advice</h3>
          <p>
            The Platform provides analytical tools for informational purposes only. Nothing on the Platform constitutes legal, financial, or real estate advice. All decisions regarding offer acceptance or rejection are solely your responsibility.
          </p>
          <h3>Disclaimer of Warranties</h3>
          <p>
            The Platform is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
          </p>
          <h3>Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by applicable law, Score My Offer shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or goodwill, arising from your use of or inability to use the Platform.
          </p>
        </section>

        <section className="legal-section" id="termination">
          <h2><span className="legal-section-num">10</span> Termination</h2>
          <p>
            We reserve the right to suspend or terminate your account at any time, with or without notice, for violation of these Terms or for any other reason at our sole discretion. You may terminate your account at any time by contacting us.
          </p>
          <p>
            Upon termination, your right to access the Platform ceases immediately. Provisions of these Terms that by their nature should survive termination will remain in effect.
          </p>
        </section>

        <section className="legal-section" id="governing-law">
          <h2><span className="legal-section-num">11</span> Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the United States and the state in which Score My Offer is incorporated, without regard to its conflict of law provisions.
          </p>
          <p>
            Any dispute arising out of or relating to these Terms or your use of the Platform shall be subject to the exclusive jurisdiction of the courts located in that state.
          </p>
        </section>

        <section className="legal-section" id="contact">
          <h2><span className="legal-section-num">12</span> Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding these Terms &amp; Conditions, please contact us:
          </p>
          <div className="legal-contact-box">
            <p><strong>Score My Offer</strong></p>
            <p>Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
            <p>Website: <a href="https://scoremyoffer.com">scoremyoffer.com</a></p>
          </div>
        </section>
      </main>

      <footer className="legal-footer">
        © {new Date().getFullYear()} Score My Offer. All rights reserved.
        {' · '}
        <Link to="/terms">Terms &amp; Conditions</Link>
        {' · '}
        <Link to="/privacy">Privacy Policy</Link>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
